/** Metadados do fluxo de evolução (impressos, retorno, etc.) — persistidos em `clinic_evolutions.metadata`. */

export type EvolutionPrintableCategory =
  | 'prescription'
  | 'certificate'
  | 'exam_order'
  | 'refraction'
  | 'indication'
  | 'medical_report'
  | 'report'
  | 'surgical_risk'
  | 'insurance_guide';

export interface EvolutionAttachedPrintable {
  clientKey: string;
  category: EvolutionPrintableCategory;
  templateId?: string;
  templateName: string;
  bodyText: string;
  /** Documento oficial salvo no cadastro (impressão via API igual às páginas Receituários/Atestados). */
  linkedDocumentId?: string;
  linkedDocumentType?: 'prescription' | 'certificate' | 'exam_order' | 'medical_report';
  /** `memed`: o original com validade legal é o PDF assinado hospedado pela Memed. */
  linkedDocumentOrigin?: 'memed';
}

/**
 * Natureza do ato clínico, declarada em Configurações > Procedimentos
 * (`clinic_procedure_categories.clinical_act_type`). É o que decide o funil de
 * CRM no desfecho da evolução — nada é hardcoded por especialidade.
 */
export type ClinicalActType = 'consultation' | 'exam' | 'procedure' | 'surgery';

/** De onde veio a indicação — usado só para explicar a detecção ao médico. */
export type EvolutionIndicationSource = 'catalog' | 'specialty_grid' | 'free_text';

/**
 * Uma indicação da evolução com a identidade do procedimento preservada.
 * Persistido em `clinic_evolutions.procedures_performed`, que antes guardava
 * apenas `{ name, notes }` — sem isso não há como classificar o desfecho.
 */
export interface EvolutionIndicationItem {
  name: string;
  notes?: string;
  procedure_id?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  /** Nulo = não foi possível classificar; o desfecho pede escolha manual. */
  act_type?: ClinicalActType | null;
  source?: EvolutionIndicationSource;
}

/** Lê `procedures_performed` em qualquer um dos formatos (legado ou atual). */
export function parseEvolutionIndicationItems(raw: any): EvolutionIndicationItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry: any): EvolutionIndicationItem | null => {
      if (typeof entry === 'string') return entry.trim() ? { name: entry.trim() } : null;
      const name = String(entry?.name || entry?.label || '').trim();
      if (!name) return null;
      return {
        name,
        notes: entry?.notes || undefined,
        procedure_id: entry?.procedure_id ?? null,
        category_id: entry?.category_id ?? null,
        category_name: entry?.category_name ?? null,
        act_type: (entry?.act_type as ClinicalActType) ?? null,
        source: entry?.source as EvolutionIndicationSource | undefined,
      };
    })
    .filter((i): i is EvolutionIndicationItem => i !== null);
}

/** Une listas de indicações sem duplicar pelo nome, preferindo a entrada classificada. */
export function mergeEvolutionIndicationItems(
  current: EvolutionIndicationItem[],
  incoming: EvolutionIndicationItem[],
): EvolutionIndicationItem[] {
  const byName = new Map<string, EvolutionIndicationItem>();
  for (const item of [...current, ...incoming]) {
    const key = item.name.trim().toLowerCase();
    if (!key) continue;
    const existing = byName.get(key);
    // Reprocessar a mesma indicação pode trazer classificação onde antes não havia.
    if (!existing || (!existing.act_type && item.act_type)) byName.set(key, item);
  }
  return Array.from(byName.values());
}

/**
 * Formato antigo do painel "Retorno". Mantido porque impressos, timeline e o
 * detalhe do paciente ainda renderizam a partir dele. Quem decide o CRM hoje é
 * `evolutionOutcomeModel`, que converte nos dois sentidos.
 */
export interface EvolutionReturnWorkflow {
  returnWithExams: boolean;
  returnForSurgery: boolean;
  /** dias até retorno */
  returnInDays: string;
  /** dias para reavaliação */
  reevaluationInDays: string;
  dischargeNoRecall: boolean;
  /** alta agora, mas reconvocar depois (funil de recalls) */
  dischargeWithRecall: boolean;
  /**
   * Período de reconvocação (recall) em dias. Pode ser definido MESMO com
   * indicação de cirurgia — se o paciente não realizar o procedimento, a IA
   * mantém o recall (funil de recalls) como card secundário.
   */
  recallInDays: string;
}
